/* eslint-disable @typescript-eslint/no-explicit-any,  @typescript-eslint/no-unused-vars */
import { trace } from '@opentelemetry/api'
import { CompositePropagator, W3CBaggagePropagator, W3CTraceContextPropagator } from '@opentelemetry/core'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'

// Configuration
const otlpEndpoint = process.env.REACT_APP_OTEL_EXPORTER_OTLP_ENDPOINT || 'http://localhost:4318'
const serviceName = process.env.REACT_APP_OTEL_SERVICE_NAME || 'numeo-web-app-dev'
const environment = process.env.REACT_APP_ENVIRONMENT || 'development'

// Global state
let globalProvider: WebTracerProvider | null = null
let globalTracer: any = null
let _telemetryInitialized = false
let initializationPromise: Promise<void> | null = null

// No-op tracer for fallback
class NoOpTracer {
    startSpan() {
        return {
            setAttributes: () => {},
            setStatus: () => {},
            recordException: () => {},
            addEvent: () => {},
            end: () => {},
            spanContext: () => ({ spanId: 'noop', traceId: 'noop' }),
        }
    }
}

const noOpTracer = new NoOpTracer()

const initializeTelemetry = async (): Promise<void> => {
    try {
        console.log('Starting OpenTelemetry initialization...')

        // Skip if telemetry is disabled
        if (process.env.REACT_APP_ENABLE_TELEMETRY !== 'true') {
            globalTracer = noOpTracer
            return
        }

        // Check if endpoint is configured
        if (!process.env.REACT_APP_OTEL_EXPORTER_OTLP_ENDPOINT) {
            console.warn('REACT_APP_OTEL_EXPORTER_OTLP_ENDPOINT not configured')
            globalTracer = noOpTracer
            return
        }

        // Create resource
        const resource = resourceFromAttributes({
            'service.version': '1.0.0',
            'service.environment': environment,
            'service.name': serviceName || 'numeo-web-app-dev',
            'service.namespace': 'numeo',
            'deployment.environment': 'web',
            'service.type': 'api',
            'telemetry.sdk.name': 'opentelemetry',
            'telemetry.sdk.language': 'nodejs',
        })

        // Create trace exporter
        const traceExporter = new OTLPTraceExporter({
            url: `${otlpEndpoint}/v1/traces`,
            headers: {
                'service-name': serviceName || 'numeo-web-app-dev',
                'Content-Type': 'application/json',
            },
        })

        // Configure span processor with optimized settings for single traces
        const spanProcessor = new BatchSpanProcessor(traceExporter, {
            scheduledDelayMillis: 10000, // Export every 10 second
            exportTimeoutMillis: 10000, // 10 second timeout
            maxExportBatchSize: 50, // Batch multiple spans together
            maxQueueSize: 1000, // Allow larger queue for complex traces
        })

        const provider = new WebTracerProvider({
            resource,
            spanProcessors: [spanProcessor],
        })

        // Store references for debugging and access
        globalProvider = provider
        ;(window as any).__OTEL_TRACER_PROVIDER__ = provider
        ;(window as any).__OTEL_SPAN_PROCESSOR__ = spanProcessor

        provider.register({
            propagator: new CompositePropagator({
                propagators: [new W3CBaggagePropagator(), new W3CTraceContextPropagator()],
            }),
        })

        console.log('Registered tracer provider')

        // Get real tracer
        const realTracer = trace.getTracer(serviceName || 'numeo-web-app-dev', '1.0.0')
        globalTracer = realTracer

        _telemetryInitialized = true

        console.log('OpenTelemetry initialized successfully')
    } catch (error: any) {
        console.error('OpenTelemetry initialization failed:', error)
        console.error('Stack trace:', error.stack)
        globalTracer = noOpTracer
        _telemetryInitialized = false
    }
}

// Ensure initialization happens only once
export const ensureTelemetryInitialized = async (): Promise<void> => {
    if (!initializationPromise) {
        initializationPromise = initializeTelemetry()
    }
    return initializationPromise
}

// Export tracer getter
export const getTracer = () => {
    if (!globalTracer) {
        console.warn('Tracer requested before initialization, returning no-op')
        return noOpTracer
    }

    if (globalTracer === noOpTracer) {
        console.warn('Using no-op tracer. Telemetry may be disabled or failed to initialize')
    }

    return globalTracer
}

// Force export function for manual testing
export const forceExport = async () => {
    try {
        if (globalProvider) {
            const spanProcessors = (globalProvider as any)._spanProcessors
            if (spanProcessors) {
                for (const processor of spanProcessors) {
                    if (processor.forceFlush) {
                        await processor.forceFlush()
                        console.log('Forced export completed')
                    }
                }
            }
        }
    } catch (error) {
        console.error('Failed to force export:', error)
    }
}

export default ensureTelemetryInitialized
